import { Button, Group, Stack, Text, TextInput } from '@mantine/core';
import { closeModal } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { handleSubmitError } from '@utils/forms';

import {
  CreatePathologyRequest,
  Pathology,
  UpdatePathologyRequest,
} from '@interfaces/pathologies.interface';

import {
  useCreatePathologyMutation,
  useUpdatePathologyMutation,
} from '@api/pathologies.api';

import useFormEnhanced from '@hooks/useFormEnhanced';

export const UPSERT_PATHOLOGY_FORM_MODAL_ID = 'upsertPathology';

interface UpsertPathologyFormProps {
  pathology?: Pathology;
}

export default function UpsertPathologyForm({
  pathology,
}: UpsertPathologyFormProps) {
  const initialValues = {
    name: pathology?.name || '',
  };

  const form = useFormEnhanced({
    initialValues,
    normalizeFields: {
      capitalizeFields: ['name'],
    },
  });

  const [createPathology, { isLoading: isLoadingCreate }] =
    useCreatePathologyMutation();
  const [updatePathology, { isLoading: isLoadingUpdate }] =
    useUpdatePathologyMutation();

  const onCancelClick = () => {
    closeModal(UPSERT_PATHOLOGY_FORM_MODAL_ID);
  };

  const onSubmit = async (values: typeof initialValues) => {
    try {
      if (pathology) {
        const data: UpdatePathologyRequest = {
          id: pathology.id,
          body: {
            name: values.name,
          },
        };

        await updatePathology(data).unwrap();

        notifications.show({
          title: 'Patologia modificata',
          message: 'Patologia modificata con successo.',
        });
      } else {
        const data: CreatePathologyRequest = {
          name: values.name,
        };

        await createPathology(data).unwrap();

        notifications.show({
          title: 'Patologia creata',
          message: 'Patologia creata con successo.',
        });
      }

      closeModal(UPSERT_PATHOLOGY_FORM_MODAL_ID);
    } catch (e) {
      handleSubmitError(e, form);
    }
  };

  return (
    <form onSubmit={form.onSubmit(onSubmit)}>
      <Stack gap="sm">
        <TextInput
          data-autofocus
          required
          label="Nome"
          {...form.getInputProps('name')}
        />
      </Stack>

      {form.errors.general && (
        <Text c="red" size="xs">
          {form.errors.general}
        </Text>
      )}

      <Group justify="right" mt="xl">
        <Button
          onClick={onCancelClick}
          variant="default"
          loading={isLoadingCreate || isLoadingUpdate}
        >
          Annulla
        </Button>
        <Button type="submit" loading={isLoadingCreate || isLoadingUpdate}>
          Conferma
        </Button>
      </Group>
    </form>
  );
}
